import {
  DataGrid,
  EmptyState,
  Icon,
  theme,
  Spinner,
  Avatar,
  AvatarSize,
  TableCell,
  IconDictionary,
  Chip,
  CustomIconName,
  PopUp,
  Button,
} from '@ketch-com/deck'
import { Fragment, useMemo, useRef, useState } from 'react'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import { useDataSentryTrackersList } from 'api/dataSentry/queries/useDataSentryTrackersList'
import { Box, Typography, Tooltip } from '@mui/material'
import { DataSentryTableFilters } from 'pages/dataSentry/components/DataSentryTableFilters'
import { useTableFilters } from 'pages/dataSentry/hooks/useTableFilters'
import {
  AuthorizationStatus as TrackerAuthorizationStatus,
  ControlStatus as TrackerControlStatus,
  TrackerListItem,
  TrackerListItemOrigin,
  TrackersLineageNode,
} from '@ketch-com/windlass/dist/dataSentry/v1/trackers/trackers'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { avatarIconByOrigin } from '../constants'
import { getSystemName } from 'pages/dataSentry/utils/getSystemName'
import { MaybeNull } from 'interfaces'

export const CommonTrackerLineages: React.FC = () => {
  const [trackerAuthorizationStatus, setTrackerAuthorizationStatus] = useState<TrackerAuthorizationStatus>(
    TrackerAuthorizationStatus.AUTHORIZED,
  )
  const [trackerControlStatus, setTrackerControlStatus] = useState<TrackerControlStatus>(
    TrackerControlStatus.UNSPECIFIED,
  )

  const [moreLineages, setMoreLineages] = useState<MaybeNull<TrackersLineageNode[]>>(null)
  const {
    searchQuery,
    debouncedSearchQuery,
    setSearchQuery,
    dataSystemFilterValue,
    setDataSystemFilterValue,
    paginationModel,
    setPaginationModel,
  } = useTableFilters()
  const { selectedTimeRange, selectedPropertyCode, setTrackerOverview } = useDataSentryContext()

  const { data: trackersList, isLoading } = useDataSentryTrackersList({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedPropertyCode,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      query: debouncedSearchQuery,
      'data-system-code': dataSystemFilterValue?.code,
      'authorization-status': trackerAuthorizationStatus,
      'control-status': trackerControlStatus,
    },
  })

  const rowCountRef = useRef(trackersList?.total || 0)
  const rowCount = useMemo(() => {
    if (trackersList?.total !== undefined) {
      rowCountRef.current = trackersList?.total
    }
    return rowCountRef.current
  }, [trackersList?.total])

  const columns: GridColDef<TrackerListItem>[] = useMemo(
    () => [
      {
        align: 'left',
        field: 'name',
        headerName: '',
        flex: 3,
        headerAlign: 'left',
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
          let hasLineage = false
          return (
            <TableCell>
              <Box display="flex" alignItems="center" gap={1} maxWidth="100%">
                <Icon name={IconDictionary.OGlobe} iconColor={theme.palette.Text.Secondary} />

                <Box display="flex" flexDirection="column" gap={2}>
                  {row?.lineages?.map(lineage => {
                    hasLineage = true

                    const totalLineages = lineage.length
                    const allowedLineage = 4
                    const hasMoreLineages = totalLineages > allowedLineage
                    const moreLineages: TrackersLineageNode[] = []
                    const lineageList = lineage
                      ?.map((l, i) => {
                        if (i >= allowedLineage) {
                          moreLineages.push(l)
                          return null
                        }
                        return l
                      })
                      .filter(l => l)

                    const lineageMap = lineageList?.map((lItem, i) => {
                      const url = lItem?.url || ''
                      const dataSystemLogoUrl = lItem?.dataSystem?.logoUrl || ''
                      const dataSystemName = getSystemName(lItem?.dataSystem || {})

                      return (
                        <Fragment key={i}>
                          {i > 0 ? (
                            <Icon
                              name={IconDictionary.OArrowCRight}
                              width={20}
                              height={20}
                              iconColor={theme.palette.Black.o48}
                            />
                          ) : null}
                          <Tooltip title={url}>
                            <Box display="flex" gap={1} alignItems="center">
                              {i > 0 ? (
                                dataSystemLogoUrl ? (
                                  <Avatar src={dataSystemLogoUrl} alt={dataSystemName} size={AvatarSize.small} />
                                ) : (
                                  <Avatar
                                    icon="NoLogo"
                                    size={AvatarSize.small}
                                    textColor={theme.palette.Black.o48}
                                    isLogo
                                  />
                                )
                              ) : null}
                              <Typography variant="body">{dataSystemName}</Typography>
                            </Box>
                          </Tooltip>
                        </Fragment>
                      )
                    })

                    if (hasMoreLineages) {
                      lineageMap.push(
                        <>
                          <Icon
                            name={IconDictionary.OArrowCRight}
                            width={20}
                            height={20}
                            iconColor={theme.palette.Black.o48}
                          />
                          <Chip
                            size="small"
                            label={<Typography variant="smallBody">{totalLineages - allowedLineage} more</Typography>}
                            clickable
                            onClick={() => setMoreLineages(moreLineages)}
                          />
                        </>,
                      )
                    }

                    return (
                      <Box display="flex" alignItems="center" gap={1}>
                        {lineageMap}
                      </Box>
                    )
                  })}
                </Box>

                {hasLineage ? (
                  <Icon name={IconDictionary.OArrowCRight} width={20} height={20} iconColor={theme.palette.Black.o48} />
                ) : null}

                <Chip
                  size="medium"
                  label={
                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                      <Typography variant="smallLabel">{row.name}</Typography>
                      <Typography variant="smallBody">{getSystemName(row?.dataSystem ?? {})}</Typography>
                    </Box>
                  }
                  icon={
                    <Icon
                      name={
                        avatarIconByOrigin[
                          row?.origin || TrackerListItemOrigin.TRACKER_ORIGIN_UNSPECIFIED
                        ] as CustomIconName
                      }
                      iconColor={theme.palette.Text.Secondary}
                    />
                  }
                  onClick={() => {
                    setTrackerOverview(row)
                  }}
                />
              </Box>
            </TableCell>
          )
        },
      },
    ],
    [setTrackerOverview],
  )

  const isLoadingTrackersList = isLoading
  const displayCommonTrackerLineagesEmptyState =
    !isLoadingTrackersList && !trackersList?.trackers?.length && !debouncedSearchQuery
  const displayCommonTrackerLineages =
    !isLoadingTrackersList &&
    (trackersList?.trackers?.length || (!trackersList?.trackers?.length && debouncedSearchQuery))

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ backgroundColor: theme.palette.Common.Container }}
      borderRadius="11px"
      px={2.5}
      py={2}
    >
      <Typography pb={1} variant="h4">
        Common Tracker Lineages
      </Typography>

      <DataSentryTableFilters
        searchQuery={searchQuery}
        handleSearchInputChange={setSearchQuery}
        dataSystemFilterValue={dataSystemFilterValue}
        setDataSystemFilterValue={setDataSystemFilterValue}
        setTrackerAuthorizationStatus={setTrackerAuthorizationStatus}
        trackerAuthorizationStatus={trackerAuthorizationStatus}
        trackerControlStatus={trackerControlStatus}
        setTrackerControlStatus={setTrackerControlStatus}
        setPaginationModel={setPaginationModel}
      />

      {isLoadingTrackersList ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
          <Spinner size="32px" thickness={2} />
        </Box>
      ) : null}

      {displayCommonTrackerLineagesEmptyState ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="350px">
          <EmptyState
            size="small"
            colorTheme={theme.palette.Text.Secondary}
            sx={{ backgroundColor: theme.palette.Black.o8, borderRadius: 4 }}
            customIcon={<Icon name="OTableView" width="48px" height="48px" iconColor={theme.palette.Black.o48} />}
            title="No Data"
            subTitle="Review your settings and connections, or wait for the scan to finish processing."
          />
        </Box>
      ) : null}

      {displayCommonTrackerLineages ? (
        <DataGrid
          sx={{
            border: 0,
            ...(isLoadingTrackersList
              ? {
                  '& .MuiDataGrid-virtualScroller': {
                    height: '300px',
                  },
                }
              : {}),
            '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
              display: 'none',
            },
          }}
          autosizeOnMount
          autosizeOptions={{
            expand: true,
            includeHeaders: true,
            includeOutliers: false,
          }}
          getRowHeight={() => 'auto'}
          columns={columns}
          getRowId={row => row.name}
          rows={trackersList?.trackers || []}
          checkboxSelection={false}
          hideFooter={false}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnPinning
          disableColumnReorder
          disableColumnResize
          disableRowHoverStates
          disableChildrenSorting={false}
          loading={isLoadingTrackersList}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 20]}
          noRowsOverlayProps={{ buttonTitle: '' }}
        />
      ) : null}

      {moreLineages ? (
        <PopUp
          title="Common Tracker Lineages"
          popUpWidth="780px"
          variant="dialog"
          onClose={() => setMoreLineages(null)}
          popUpActionChildren={
            <Button color="secondary" size="large" onClick={() => setMoreLineages(null)}>
              Close
            </Button>
          }
        >
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            {moreLineages?.map((l, i) => {
              const url = l.url || ''
              const dataSystemLogoUrl = l?.dataSystem?.logoUrl || ''
              const dataSystemName = getSystemName(l?.dataSystem || {})
              return (
                <Fragment key={i}>
                  {i > 0 ? (
                    <Icon
                      name={IconDictionary.OArrowCDown}
                      width={20}
                      height={20}
                      iconColor={theme.palette.Black.o48}
                    />
                  ) : null}
                  <Tooltip title={url}>
                    <Box display="flex" gap={1} alignItems="center">
                      {dataSystemLogoUrl ? (
                        <Avatar src={dataSystemLogoUrl} alt={dataSystemName} size={AvatarSize.small} />
                      ) : (
                        <Avatar icon="NoLogo" size={AvatarSize.small} textColor={theme.palette.Black.o48} isLogo />
                      )}
                      <Typography variant="body">{dataSystemName}</Typography>
                    </Box>
                  </Tooltip>
                </Fragment>
              )
            })}
          </Box>
        </PopUp>
      ) : null}
    </Box>
  )
}
